/**
 * DemoModeContext.jsx - Global Demo Mode State Management
 * 
 * PHASE H: Full Demo Mode Implementation
 * 
 * Provides:
 * - Global demo mode state
 * - Step navigation
 * - Surface transitions
 * - Skip functionality
 * - Reproducible behavior
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Demo Mode Steps Configuration
const DEMO_STEPS = [
  {
    id: 'welcome',
    surface: '/',
    title: 'Welcome to Granullo',
    subtitle: 'Infrastructure for decisions that cannot afford ambiguity',
    description: 'Granullo is an institutional intelligence layer that observes commitments, execution signals, and structural exposure across complex decision networks. Granullo does not move money.',
    instruction: 'This guided walkthrough will demonstrate the three core surfaces of the platform.',
    highlight: null,
    position: 'center'
  },
  {
    id: 'observatory-intro',
    surface: '/observatory',
    title: 'Network Observatory',
    subtitle: 'Step 1 of 3: Observe',
    description: 'The Observatory provides a structural view of the decision network. Here you can observe the topology of participants, execution nodes, and governing entities.',
    instruction: 'Notice the three-column pipeline showing signal flow from left to right.',
    highlight: '[data-testid="command-center-graph"]',
    position: 'bottom-right'
  },
  {
    id: 'observatory-insights',
    surface: '/observatory',
    title: 'Active Insights',
    subtitle: 'Understanding Network Signals',
    description: 'The system automatically detects coverage gaps, bottlenecks, and integrity signals. Each insight card highlights a specific area of the network.',
    instruction: 'Click on any insight card to see the affected nodes highlighted in the graph.',
    highlight: '[data-testid="active-insights"]',
    position: 'left'
  },
  {
    id: 'observatory-scenario',
    surface: '/observatory',
    title: 'Scenario Mode',
    subtitle: 'What-If Analysis',
    description: 'Enable Scenario Mode to see how network changes would affect the structure. This is a preview of the full Simulation surface.',
    instruction: 'The Scenario Mode toggle allows quick what-if exploration without leaving the Observatory.',
    highlight: '[data-testid="scenario-mode-toggle"]',
    position: 'top-right'
  },
  {
    id: 'simulation-intro',
    surface: '/simulation',
    title: 'Network Simulation',
    subtitle: 'Step 2 of 3: Simulate',
    description: 'The Simulation surface allows you to model expansion scenarios and stress tests. See how changes to participants, execution nodes, and capacity affect network health.',
    instruction: 'Select a scenario preset or adjust the sliders manually to model different outcomes.',
    highlight: '[data-testid="scenario-presets"]',
    position: 'right'
  },
  {
    id: 'simulation-insight',
    surface: '/simulation',
    title: 'System Interpretation',
    subtitle: 'AI-Powered Analysis',
    description: 'The Scenario Insight Panel provides an automated interpretation of your simulation results. The system explains the structural implications of each scenario.',
    instruction: 'Watch how the interpretation updates as you adjust parameters.',
    highlight: '[data-testid="scenario-insight-panel"]',
    position: 'left'
  },
  {
    id: 'mission-control-intro',
    surface: '/mission-control',
    title: 'Mission Control',
    subtitle: 'Step 3 of 3: Operate',
    description: 'Mission Control is your operational command center. Monitor network health, integrity signals, and decision exposure in real-time.',
    instruction: 'The Situational Awareness tab provides a system-level summary of current operations.',
    highlight: '[data-testid="situational-tab"]',
    position: 'bottom'
  },
  {
    id: 'mission-control-health',
    surface: '/mission-control',
    title: 'Network Health',
    subtitle: 'Real-Time Monitoring',
    description: 'The Health tab shows detailed status of all network components. Each indicator provides instant visibility into system performance.',
    instruction: 'Switch to the Health tab to see component-level status.',
    highlight: '[data-testid="mission-control-tabs"]',
    position: 'top'
  },
  {
    id: 'mission-control-integrity',
    surface: '/mission-control',
    title: 'Integrity Signals',
    subtitle: 'Trust Verification',
    description: 'The Integrity tab displays verification status of all trust metrics. Hash chains, deterministic state, and audit trails are continuously validated.',
    instruction: 'Switch to the Integrity tab to see verification signals.',
    highlight: '[data-testid="mission-control-tabs"]',
    position: 'top'
  },
  {
    id: 'complete',
    surface: '/mission-control',
    title: 'Demo Complete',
    subtitle: 'The Granullo Platform',
    description: 'You have completed the guided walkthrough of the Granullo platform. The three surfaces work together to provide complete operational intelligence.',
    instruction: 'Continue exploring the platform or restart the demo from any surface.',
    highlight: null,
    position: 'center'
  }
];

// Context
const DemoModeContext = createContext(null);

// Provider Component
export function DemoModeProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasCompletedOnce, setHasCompletedOnce] = useState(false);

  const currentStep = DEMO_STEPS[currentStepIndex];
  const totalSteps = DEMO_STEPS.length;
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;

  // Start demo mode
  const startDemo = useCallback(() => {
    setIsActive(true);
    setCurrentStepIndex(0);
    setIsTransitioning(false);
  }, []);

  // Stop demo mode
  const stopDemo = useCallback(() => {
    setIsActive(false);
    setCurrentStepIndex(0);
    setIsTransitioning(false);
  }, []);

  // Skip to end
  const skipDemo = useCallback(() => {
    setIsActive(false);
    setCurrentStepIndex(0);
    setIsTransitioning(false);
  }, []);

  // Navigate to next step
  const nextStep = useCallback(() => {
    if (currentStepIndex >= totalSteps - 1) {
      setHasCompletedOnce(true);
      stopDemo();
      return;
    }

    const nextIndex = currentStepIndex + 1;
    const nextStepData = DEMO_STEPS[nextIndex];

    // Check if we need to navigate to a different surface
    if (nextStepData.surface !== location.pathname) {
      setIsTransitioning(true);
      navigate(nextStepData.surface);
      
      // Wait for navigation, then show next step
      setTimeout(() => {
        setCurrentStepIndex(nextIndex);
        setIsTransitioning(false);
      }, 800);
    } else {
      setCurrentStepIndex(nextIndex);
    }
  }, [currentStepIndex, totalSteps, location.pathname, navigate, stopDemo]);

  // Navigate to previous step
  const prevStep = useCallback(() => {
    if (currentStepIndex <= 0) return;

    const prevIndex = currentStepIndex - 1;
    const prevStepData = DEMO_STEPS[prevIndex];

    // Check if we need to navigate to a different surface
    if (prevStepData.surface !== location.pathname) {
      setIsTransitioning(true);
      navigate(prevStepData.surface);
      
      setTimeout(() => {
        setCurrentStepIndex(prevIndex);
        setIsTransitioning(false);
      }, 800);
    } else {
      setCurrentStepIndex(prevIndex);
    }
  }, [currentStepIndex, location.pathname, navigate]);

  // Jump to specific step
  const goToStep = useCallback((stepIndex) => {
    if (stepIndex < 0 || stepIndex >= totalSteps) return;

    const targetStep = DEMO_STEPS[stepIndex];

    if (targetStep.surface !== location.pathname) {
      setIsTransitioning(true);
      navigate(targetStep.surface);
      
      setTimeout(() => {
        setCurrentStepIndex(stepIndex);
        setIsTransitioning(false);
      }, 800);
    } else {
      setCurrentStepIndex(stepIndex);
    }
  }, [totalSteps, location.pathname, navigate]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        skipDemo();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        nextStep();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, nextStep, prevStep, skipDemo]);

  const value = {
    // State
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    progress,
    isTransitioning,
    hasCompletedOnce,
    steps: DEMO_STEPS,
    
    // Actions
    startDemo,
    stopDemo,
    skipDemo,
    nextStep,
    prevStep,
    goToStep
  };

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
}

// Hook to use demo mode
export function useDemoMode() {
  const context = useContext(DemoModeContext);
  if (!context) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}

export { DEMO_STEPS };
export default DemoModeContext;
